/**
 * Creator Library niche chips (decision 88): "All" plus the team's niches, multi-select (any of the picked ones). The
 * page asks the server for the filtered list (`GET /api/explore?niches=a,b`; paged, decision 100). Also the niche chips on creator cards, the
 * creator header and the public share page. No router import, so tests render it without a browser.
 */
import { cn } from '@vw/ui';

/** The creator's niches (decision 88) as small chips; nothing when it has none. */
export function NicheChips({ niches, className }: { niches: Array<{ slug: string; name: string }> | undefined; className?: string }) {
  if (!niches?.length) return null;
  return (
    <div className={cn('flex flex-wrap gap-1', className)} aria-label="Niches">
      {niches.map((n) => <span key={n.slug} className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">{n.name}</span>)}
    </div>
  );
}

export interface ExploreNiche { slug: string; name: string; count: number }
/**
 * The Creator Library request (decisions 88, 100): picked niches, search, sort and page all run on the server. Defaults
 * (no search, "recent", the first page, 50 a page) are left out, so the common request stays one cacheable URL.
 */
export function exploreUrl(niches: string[], o: { q?: string; sort?: string; page?: number; pageSize?: number } = {}): string {
  const p = new URLSearchParams();
  if (niches.length) p.set('niches', niches.join(','));
  if (o.q?.trim()) p.set('q', o.q.trim());
  if (o.sort && o.sort !== 'recent') p.set('sort', o.sort);
  if (o.page) p.set('page', String(o.page));
  if (o.pageSize) p.set('pageSize', String(o.pageSize));
  const qs = p.toString();
  return qs ? `/api/explore?${qs}` : '/api/explore';
}

const chip = (on: boolean) => on ? 'shrink-0 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground' : 'shrink-0 rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground';

/** "All" plus one chip per niche with published creators (the selected ones always show); any of the selected match. */
export function NicheFilter({ niches, total, selected, onChange }: { niches: ExploreNiche[]; total: number; selected: string[]; onChange: (s: string[]) => void }) {
  const shown = niches.filter((n) => n.count > 0 || selected.includes(n.slug));
  if (!shown.length) return null;
  const toggle = (slug: string) => onChange(selected.includes(slug) ? selected.filter((s) => s !== slug) : [...selected, slug]);
  return (
    <div className="no-scrollbar flex items-center gap-2 overflow-x-auto" role="group" aria-label="Filter by niche">
      <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Niches:</span>
      <button type="button" aria-pressed={!selected.length} onClick={() => onChange([])} className={chip(!selected.length)}>All ({total})</button>
      {shown.map((n) => <button type="button" key={n.slug} aria-pressed={selected.includes(n.slug)} onClick={() => toggle(n.slug)} className={chip(selected.includes(n.slug))}>{n.name} ({n.count})</button>)}
    </div>
  );
}
